import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RiMailSendLine, RiSearchLine, RiCheckLine, RiCloseLine,
  RiAlertLine, RiEyeLine, RiRefreshLine, RiAddLine, RiDeleteBinLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import {
  adminGetCampaignRecipients, adminSendCampaign,
  adminAddCampaignRecipient, adminRemoveCampaignRecipient,
  adminGetCampaignJob, adminCancelCampaignJob,
} from '../../api/admin.api';
import { LoadingScreen, EmptyState } from '../../components/shared/UI';

/* ══════════════════════════════════════════════════════════════
   EMAIL CAMPAIGN

   Compose one message and send it to the pre-registration waitlist.

   Two deliberate frictions, because this is the one screen in the
   admin panel that does something irreversible to people outside the
   building:

   • Nothing is selected when the page loads. Select-all is one click
     away, but it has to be a click — a page that arrives with 400
     people ticked is one stray Enter away from a mistake you cannot
     take back.

   • Sending is behind a typed confirmation showing the exact count.
     The same pattern as deleting an account, for the same reason.
══════════════════════════════════════════════════════════════ */

export default function CampaignManager() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(() => new Set());

  const [subject, setSubject] = useState('');
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);   // live progress while a run is going
  const [preview, setPreview] = useState(false);

  const load = useCallback(() => {
    setError('');
    setRows(null);
    adminGetCampaignRecipients()
      .then(res => setRows(res.data))
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return q ? rows.filter(r => r.email.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const toggle = email => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });

  /* Select-all acts on the current filter, not the whole list — otherwise
     searching then clicking it silently selects people you cannot see. */
  const allFilteredPicked = filtered.length > 0 && filtered.every(r => picked.has(r.email));
  const toggleAllFiltered = () => setPicked(prev => {
    const next = new Set(prev);
    if (allFilteredPicked) filtered.forEach(r => next.delete(r.email));
    else filtered.forEach(r => next.add(r.email));
    return next;
  });

  const addOne = useCallback(async e => {
    e?.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    setAddError('');
    try {
      const res = await adminAddCampaignRecipient(email);
      setRows(prev => [res.data, ...(prev || [])]);
      /* Tick it straight away — someone who just typed an address
         plainly intends to mail it. */
      setPicked(prev => new Set(prev).add(res.data.email));
      setNewEmail('');
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  }, [newEmail]);

  const removeOne = useCallback(async row => {
    setAddError('');
    try {
      await adminRemoveCampaignRecipient(row._id);
      setRows(prev => (prev || []).filter(r => r._id !== row._id));
      setPicked(prev => {
        const next = new Set(prev);
        next.delete(row.email);
        return next;
      });
    } catch (err) {
      setAddError(err.message);
    }
  }, []);

  const send = useCallback(async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await adminSendCampaign({
        recipients: [...picked],
        subject: subject.trim(),
        heading: heading.trim() || subject.trim(),
        body,
        ctaLabel: ctaLabel.trim(),
        ctaUrl: ctaUrl.trim(),
      });
      /* The server has accepted the job, not finished it. Progress
         arrives from polling below. */
      setJob({ id: res.data.jobId, total: res.data.total, done: 0, running: true, sent: 0, failed: [] });
      setConfirming(false);
      setTyped('');
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setSending(false);
    }
  }, [picked, subject, heading, body, ctaLabel, ctaUrl]);

  /* Poll while a run is going. Stops as soon as the server says it has
     finished, and survives a page reload only in the sense that the run
     itself continues — the progress bar does not come back. */
  useEffect(() => {
    if (!job?.id || !job.running) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const res = await adminGetCampaignJob(job.id);
        if (!alive) return;
        setJob(prev => ({ ...prev, ...res.data }));
        if (!res.data.running) {
          setResult({
            ok: res.data.failed.length === 0,
            message: res.data.cancelled
              ? `Stopped after ${res.data.sent} of ${res.data.total}.`
              : `Sent to ${res.data.sent} of ${res.data.total}.`,
            failed: res.data.failed,
            rejected: res.data.rejected,
          });
        }
      } catch (_) { /* a dropped poll is not a failed send */ }
    };
    const id = setInterval(tick, 1500);
    tick();
    return () => { alive = false; clearInterval(id); };
  }, [job?.id, job?.running]);

  const cancel = useCallback(async () => {
    if (!job?.id) return;
    try { await adminCancelCampaignJob(job.id); } catch (_) { /* it may have just finished */ }
  }, [job]);

  if (error && !rows) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: C.red, fontSize: 13, marginBottom: 16 }}>{error}</div>
        <button className="btn-ghost" onClick={load}><RiRefreshLine size={15} /> Try again</button>
      </div>
    );
  }
  if (!rows) return <LoadingScreen label="Loading the waitlist" />;

  const ready = picked.size > 0 && subject.trim() && body.trim();
  const paragraphs = body.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);

  return (
    <div className="page-container cmp-page anim-fade-up">
      <header className="cmp-head">
        <div>
          <h1>Email campaign</h1>
          <p>Write once, send to the people who asked to hear from you.</p>
        </div>
        <span className="cmp-total">{rows.length} on the waitlist</span>
      </header>

      <div className="cmp-grid">
        {/* ── Recipients ── */}
        <section className="ks-card cmp-list">
          <div className="cmp-list-head">
            <h2>Recipients</h2>
            <span className={picked.size ? 'cmp-count on' : 'cmp-count'}>{picked.size} selected</span>
          </div>

          <form className="cmp-add" onSubmit={addOne}>
            <input
              className="ks-input"
              type="email"
              placeholder="Add an address…"
              value={newEmail}
              onChange={e => { setNewEmail(e.target.value); setAddError(''); }}
            />
            <button className="btn-primary" disabled={adding || !newEmail.trim()}>
              {adding ? <div className="spinner" /> : <RiAddLine size={16} />}
            </button>
          </form>
          {addError && <div className="cmp-add-error">{addError}</div>}

          <div className="search-wrap" style={{ marginBottom: 10 }}>
            <RiSearchLine size={15} />
            <input
              className="ks-input"
              placeholder="Filter by email"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="cmp-bulk">
            <button className="btn-ghost" onClick={toggleAllFiltered} disabled={!filtered.length}>
              {allFilteredPicked ? 'Deselect' : 'Select'} {search ? 'these' : 'all'} ({filtered.length})
            </button>
            {picked.size > 0 && (
              <button className="btn-ghost" onClick={() => setPicked(new Set())}>Clear</button>
            )}
          </div>

          {rows.length === 0 ? (
            <EmptyState
              icon={RiMailSendLine}
              title="Nobody has pre-registered yet"
              sub="Addresses collected from the landing page will show up here."
            />
          ) : (
            <ul className="cmp-rows">
              {filtered.map(r => {
                const on = picked.has(r.email);
                return (
                  <li key={r.email} className="cmp-row-wrap">
                    <button
                      type="button"
                      className={`cmp-row${on ? ' on' : ''}`}
                      onClick={() => toggle(r.email)}
                      aria-pressed={on}
                    >
                      <span className="cmp-check">{on && <RiCheckLine size={13} />}</span>
                      <span className="cmp-email">{r.email}</span>
                      <span className="cmp-src">{r.source}</span>
                    </button>
                    <button
                      type="button"
                      className="cmp-remove"
                      title={`Remove ${r.email} from the list`}
                      aria-label={`Remove ${r.email}`}
                      onClick={() => removeOne(r)}
                    >
                      <RiDeleteBinLine size={13} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ── Compose ── */}
        <section className="ks-card cmp-compose">
          <h2>Message</h2>

          <label className="cmp-field">
            Subject
            <input className="ks-input" value={subject} maxLength={120}
                   placeholder="KitsuSpeak is open"
                   onChange={e => setSubject(e.target.value)} />
          </label>

          <label className="cmp-field">
            Heading <small>shown at the top of the email — defaults to the subject</small>
            <input className="ks-input" value={heading} maxLength={120}
                   placeholder={subject || 'Your early access is ready'}
                   onChange={e => setHeading(e.target.value)} />
          </label>

          <label className="cmp-field">
            Body <small>a blank line starts a new paragraph</small>
            <textarea className="ks-textarea" rows={9} value={body}
                      placeholder={'Hi,\n\nWe are live. Your account is ready and the first 25 grammar chapters are waiting.\n\nSee you inside.'}
                      onChange={e => setBody(e.target.value)} />
          </label>

          <div className="ks-grid-2 cmp-cta-row">
            <label className="cmp-field">
              Button label <small>optional</small>
              <input className="ks-input" value={ctaLabel} maxLength={40}
                     placeholder="Start learning"
                     onChange={e => setCtaLabel(e.target.value)} />
            </label>
            <label className="cmp-field">
              Button link
              <input className="ks-input" value={ctaUrl} placeholder="https://…"
                     onChange={e => setCtaUrl(e.target.value)} />
            </label>
          </div>

          <div className="cmp-actions">
            <button className="btn-ghost" onClick={() => setPreview(p => !p)} disabled={!body.trim()}>
              <RiEyeLine size={15} /> {preview ? 'Hide' : 'Preview'}
            </button>
            <button className="btn-primary" disabled={!ready} onClick={() => setConfirming(true)}>
              <RiMailSendLine size={15} /> Send to {picked.size || '…'}
            </button>
          </div>

          {preview && body.trim() && (
            <div className="cmp-preview">
              <div className="cmp-preview-sub">Subject: {subject || '(none)'}</div>
              <div className="cmp-preview-card">
                <strong>{heading.trim() || subject || 'Heading'}</strong>
                {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
                {ctaLabel.trim() && ctaUrl.trim() && (
                  <span className="cmp-preview-btn">{ctaLabel}</span>
                )}
                <em>You are receiving this because you signed up for early access to KitsuSpeak.</em>
              </div>
            </div>
          )}

          {job?.running && (
            <div className="cmp-progress">
              <div className="cmp-progress-top">
                <strong>Sending… {job.done} of {job.total}</strong>
                <button className="btn-ghost" onClick={cancel}>Stop</button>
              </div>
              <div className="cmp-progress-bar">
                <i style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} />
              </div>
              <span className="cmp-progress-note">
                You can leave this page — the send carries on without it.
              </span>
            </div>
          )}

          {result && (
            <div className={`cmp-result${result.ok ? ' ok' : ''}`}>
              <strong>{result.message}</strong>
              {result.failed?.length > 0 && (
                <div className="cmp-result-list">
                  Failed: {result.failed.map(f => f.email).join(', ')}
                </div>
              )}
              {result.rejected?.length > 0 && (
                <div className="cmp-result-list">
                  Skipped (not on the waitlist): {result.rejected.join(', ')}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {confirming && (
        <div className="modal-overlay" onClick={() => !sending && setConfirming(false)}>
          <div className="modal-box cmp-confirm" onClick={e => e.stopPropagation()}>
            <div className="cmp-confirm-icon"><RiAlertLine size={22} /></div>
            <h3>Send to {picked.size} {picked.size === 1 ? 'person' : 'people'}?</h3>
            <p>
              This goes out immediately and cannot be recalled. Subject:
              {' '}<strong>{subject}</strong>
            </p>
            <label className="cmp-field">
              Type <strong>SEND</strong> to confirm
              <input className="ks-input" value={typed} placeholder="SEND"
                     onChange={e => setTyped(e.target.value)} />
            </label>
            <div className="cmp-confirm-actions">
              <button className="btn-ghost" disabled={sending} onClick={() => { setConfirming(false); setTyped(''); }}>
                <RiCloseLine size={15} /> Cancel
              </button>
              <button className="btn-primary" disabled={sending || typed !== 'SEND'} onClick={send}>
                {sending ? <div className="spinner" /> : <><RiMailSendLine size={15} /> Send now</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
